import { useCallback, useEffect, useRef } from 'react'

import { SNAPSHOT_DATE_FILTER_KEY } from './snapshot'
import type {
  DashboardDerivedFilters,
  DashboardFilterOptions,
  DashboardRuntimeConfig,
} from './types'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ChartResult = {
  categories?: string[]
  indicators?: Array<{ name: string; max?: number }>
  series?:
    | Array<{ name: string; data: number[] }>
    | { name: string; data: Array<{ name: string; value: number }> }
    | { value: number; name?: string }
  xCategories?: string[]
  yCategories?: string[]
  data?: [number, number, number][]
  min?: number
  max?: number
}

type MetricResult = {
  value: number
}

type CollectionResult = {
  rows: Record<string, unknown>[]
  total: number
}

export type ItemResult = {
  chart?: ChartResult
  metric?: MetricResult
  collection?: CollectionResult
  error?: string
}

type ComputeResponse = {
  results: Record<string, ItemResult>
  filterOptions?: DashboardFilterOptions
  filters?: DashboardDerivedFilters['filters']
  navigationFilters?: DashboardDerivedFilters['navigationFilters']
}

type ApiConfig = {
  baseUrl: string
  headers: Record<string, string>
  runtimeFetch?: typeof fetch
}

type InflightComputeRequest = {
  id: number
  promise: Promise<ComputeResponse>
  controller: AbortController
}

class StaleComputeRequestError extends Error {
  constructor() {
    super('Stale dashboard compute request')
    this.name = 'StaleComputeRequestError'
  }
}

function hasErrorName(error: unknown): error is { name: unknown } {
  return typeof error === 'object' && error !== null && 'name' in error
}

function isAbortError(error: unknown): boolean {
  return hasErrorName(error) && error.name === 'AbortError'
}

function isStaleComputeRequestError(error: unknown): boolean {
  return error instanceof StaleComputeRequestError
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Provides a `fetchItem` function that triggers a batch compute request
 * and returns the result for a specific item.
 *
 * Concurrent calls with the same filter state share one request. When filters
 * or refresh keys change, older backend requests are cancelled and their
 * original callers settle with an empty stale result instead of surfacing an
 * abort error in the dashboard UI.
 */
export function useDashboardCompute(
  config: DashboardRuntimeConfig,
  apiConfig: ApiConfig,
  refreshKey: number
): {
  fetchItem: (
    itemId: string,
    filterValues: Record<string, unknown[]>,
    navigationFilterValues?: Record<string, string[]>
  ) => Promise<ItemResult>
  getFilterOptions: () => DashboardFilterOptions | undefined
  getDerivedFilters: () => DashboardDerivedFilters
  getHasResponse: () => boolean
} {
  const inflightRef = useRef(new Map<string, InflightComputeRequest>())
  const latestRequestKeyRef = useRef<string>()
  const latestRequestIdRef = useRef(0)
  const requestSequenceRef = useRef(0)

  const lastResponseRef = useRef<ComputeResponse | undefined>()
  const derivedFiltersRef = useRef<DashboardDerivedFilters>({})

  useEffect(() => {
    lastResponseRef.current = undefined
    derivedFiltersRef.current = {}
    for (const request of inflightRef.current.values()) request.controller.abort()
    inflightRef.current.clear()
    latestRequestKeyRef.current = undefined
    latestRequestIdRef.current = 0
  }, [refreshKey])

  useEffect(
    () => () => {
      latestRequestKeyRef.current = undefined
      latestRequestIdRef.current = 0
      derivedFiltersRef.current = {}
      for (const request of inflightRef.current.values()) request.controller.abort()
      inflightRef.current.clear()
    },
    []
  )

  const configRef = useRef(config)
  configRef.current = config
  const apiConfigRef = useRef(apiConfig)
  apiConfigRef.current = apiConfig
  const refreshKeyRef = useRef(refreshKey)
  refreshKeyRef.current = refreshKey

  const fetchItem = useCallback(
    (
      itemId: string,
      filterValues: Record<string, unknown[]>,
      navigationFilterValues?: Record<string, string[]>
    ): Promise<ItemResult> => {
      const cfg = configRef.current
      const api = apiConfigRef.current
      const requestRefreshKey = refreshKeyRef.current

      const stringFilterValues: Record<string, string[]> = {}
      for (const [key, vals] of Object.entries(filterValues)) {
        if (Array.isArray(vals) && vals.length > 0) {
          stringFilterValues[key] = vals.map(String)
        }
      }

      let effectiveSnapshotDate: string | undefined = cfg.snapshotDate
      let cleanedNavValues: Record<string, string[]> | undefined = navigationFilterValues
      if (
        cfg.snapshot &&
        navigationFilterValues &&
        SNAPSHOT_DATE_FILTER_KEY in navigationFilterValues
      ) {
        const pillValue = navigationFilterValues[SNAPSHOT_DATE_FILTER_KEY]
        if (Array.isArray(pillValue) && pillValue[0]) {
          effectiveSnapshotDate = pillValue[0]
        }
        cleanedNavValues = { ...navigationFilterValues }
        delete cleanedNavValues[SNAPSHOT_DATE_FILTER_KEY]
      }

      const hasNavValues = cleanedNavValues && Object.keys(cleanedNavValues).length > 0

      // Derive `dateNavigatorColumn` from the spec so the backend can re-derive
      // the matching `nf_${column}` navigation filter and apply the date range
      // WHERE clause. The backend filter pipeline keys nav filters by
      // `nf_${column}`, so the FE-side filter values (keyed by the spec's
      // `navigationFilters` map keys) must align with that — see
      // `factorial-agent/src/mastra/one/derive-dashboard-filters.ts`.
      const navSpecs = cfg.navigationFilters ?? {}
      let derivedDateNavigatorColumn: string | undefined
      for (const spec of Object.values(navSpecs)) {
        if (spec.type === 'dateNavigation') {
          derivedDateNavigatorColumn = spec.column
          break
        }
      }
      const dateNavigatorColumn = cfg.dateNavigatorColumn ?? derivedDateNavigatorColumn

      const body = {
        fetchSpecs: cfg.fetchSpecs,
        items: cfg.items,
        filters: cfg.filters,
        filterValues: Object.keys(stringFilterValues).length > 0 ? stringFilterValues : undefined,
        navigationFilters: cfg.navigationFilters,
        navigationFilterValues: hasNavValues ? cleanedNavValues : undefined,
        ...(cfg.snapshot ? { snapshot: true } : {}),
        ...(cfg.snapshot && effectiveSnapshotDate ? { snapshotDate: effectiveSnapshotDate } : {}),
        ...(dateNavigatorColumn ? { dateNavigatorColumn } : {}),
      }
      const requestKey = `${requestRefreshKey}:${JSON.stringify(body)}`

      latestRequestKeyRef.current = requestKey

      const existingRequest = inflightRef.current.get(requestKey)
      if (existingRequest) {
        latestRequestIdRef.current = existingRequest.id
        return existingRequest.promise
          .then((res) => res.results[itemId] ?? { error: 'No result for item' })
          .catch((error: unknown) => {
            if (isStaleComputeRequestError(error)) return {}
            throw error
          })
      }

      for (const [key, request] of inflightRef.current.entries()) {
        if (key === requestKey) continue
        request.controller.abort()
        inflightRef.current.delete(key)
      }

      const controller = new AbortController()
      const requestId = (requestSequenceRef.current += 1)
      latestRequestIdRef.current = requestId
      const runtimeFetch = api.runtimeFetch ?? fetch

      const promise = runtimeFetch(`${api.baseUrl}/dashboard/compute`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...api.headers,
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      })
        .then(async (res) => {
          if (!res.ok) {
            throw new Error(`Dashboard compute failed: ${res.status}`)
          }
          const data: ComputeResponse = await res.json()
          if (
            refreshKeyRef.current === requestRefreshKey &&
            latestRequestKeyRef.current === requestKey &&
            latestRequestIdRef.current === requestId
          ) {
            lastResponseRef.current = data
            derivedFiltersRef.current = {
              filters: data.filters,
              navigationFilters: data.navigationFilters,
            }
          }
          return data
        })
        .catch((error: unknown) => {
          if (isAbortError(error) && latestRequestIdRef.current !== requestId) {
            throw new StaleComputeRequestError()
          }
          throw error
        })

      inflightRef.current.set(requestKey, { id: requestId, promise, controller })

      void promise.then(
        () => {
          if (inflightRef.current.get(requestKey)?.promise === promise)
            inflightRef.current.delete(requestKey)
        },
        () => {
          if (inflightRef.current.get(requestKey)?.promise === promise)
            inflightRef.current.delete(requestKey)
        }
      )

      return promise
        .then((res) => res.results[itemId] ?? { error: 'No result for item' })
        .catch((error: unknown) => {
          if (isStaleComputeRequestError(error)) return {}
          throw error
        })
    },
    []
  )

  const getFilterOptions = useCallback(() => lastResponseRef.current?.filterOptions, [])

  const getDerivedFilters = useCallback(() => derivedFiltersRef.current, [])

  const getHasResponse = useCallback(() => lastResponseRef.current !== undefined, [])

  return { fetchItem, getFilterOptions, getDerivedFilters, getHasResponse }
}
